import type {
  ExcalidrawImperativeAPI,
  BinaryFiles,
} from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";

const API_BASE = "/api";

export type AscendDrawingSummary = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

export type AscendDrawingRecord = AscendDrawingSummary & {
  data: {
    elements: readonly ExcalidrawElement[];
    appState: { viewBackgroundColor: string };
    files: BinaryFiles;
  };
};

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) {
    throw new Error(`GET ${path} failed: ${res.status}`);
  }
  return res.json();
}

async function apiSend<T>(
  method: "POST" | "PUT" | "DELETE",
  path: string,
  body: unknown,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`${method} ${path} failed: ${res.status}`);
  }
  return res.json();
}

export const listDrawings = () => apiGet<AscendDrawingSummary[]>("/drawings");

export const getDrawing = (id: string) =>
  apiGet<AscendDrawingRecord>(`/drawings/${id}`);

export const createDrawing = (
  name: string,
  data: AscendDrawingRecord["data"],
) => apiSend<AscendDrawingRecord>("POST", "/drawings", { name, data });

export const updateDrawing = (
  id: string,
  name: string,
  data: AscendDrawingRecord["data"],
) => apiSend<AscendDrawingRecord>("PUT", `/drawings/${id}`, { name, data });

export const deleteDrawing = (id: string) =>
  apiSend<{ ok: true }>("DELETE", `/drawings/${id}`, {});

export const captureScene = (
  excalidrawAPI: ExcalidrawImperativeAPI,
): AscendDrawingRecord["data"] => {
  const elements = excalidrawAPI.getSceneElements();
  const appState = excalidrawAPI.getAppState();
  const files = excalidrawAPI.getFiles();
  return {
    elements,
    appState: { viewBackgroundColor: appState.viewBackgroundColor },
    files,
  };
};

export const restoreScene = (
  excalidrawAPI: ExcalidrawImperativeAPI,
  record: AscendDrawingRecord,
) => {
  excalidrawAPI.updateScene({
    elements: record.data.elements,
    appState: record.data.appState,
  });
  if (record.data.files) {
    excalidrawAPI.addFiles(Object.values(record.data.files));
  }
};
